/**
 * 점 경로로 중첩 객체를 읽고 쓴다.
 *
 * 폼 필드가 `notices.0.title` 같은 경로로 자기 값을 가리킨다. 템플릿마다 데이터
 * 형태가 다른데 폼 컴포넌트를 5벌 만들지 않으려면, 필드가 **경로만 알고** 값을
 * 꺼내 쓸 수 있어야 한다.
 *
 * 쓰기는 항상 **새 객체를 만든다**. React 가 변경을 감지하려면 참조가 달라져야 하고,
 * 제자리 수정은 미리보기가 갱신되지 않는 조용한 버그로 이어진다.
 */

export type Path = string;

/** 경로를 조각으로. 숫자 조각은 배열 인덱스다. */
function segments(path: Path): Array<string | number> {
  return path
    .split('.')
    .filter((part) => part.length > 0)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

export function getPath(root: unknown, path: Path): unknown {
  let current = root;
  for (const key of segments(path)) {
    if (current === null || current === undefined) return undefined;
    current = (current as Record<string | number, unknown>)[key];
  }
  return current;
}

/**
 * 경로에 값을 넣은 **새** 객체를 반환한다.
 *
 * 중간 경로가 비어 있으면 만든다 — 다음 조각이 숫자면 배열, 아니면 객체.
 * 그래야 `weather.temperature` 를 처음 입력할 때 `weather` 가 자동으로 생긴다.
 */
export function setPath<T>(root: T, path: Path, value: unknown): T {
  const keys = segments(path);
  if (keys.length === 0) return value as T;

  const clone = (node: unknown, index: number): unknown => {
    const key = keys[index] as string | number;
    const isLast = index === keys.length - 1;

    const base =
      node === null || node === undefined
        ? typeof key === 'number'
          ? []
          : {}
        : Array.isArray(node)
          ? [...node]
          : { ...(node as Record<string, unknown>) };

    const container = base as Record<string | number, unknown>;
    container[key] = isLast ? value : clone(container[key], index + 1);
    return container;
  };

  return clone(root, 0) as T;
}

/** 배열 항목을 추가한 새 객체. */
export function appendItem<T>(root: T, path: Path, item: unknown): T {
  const list = (getPath(root, path) as unknown[] | undefined) ?? [];
  return setPath(root, path, [...list, item]);
}

/** 배열 항목을 제거한 새 객체. */
export function removeItem<T>(root: T, path: Path, index: number): T {
  const list = (getPath(root, path) as unknown[] | undefined) ?? [];
  return setPath(
    root,
    path,
    list.filter((_, i) => i !== index),
  );
}

/** 배열 항목을 위·아래로 옮긴 새 객체. 범위를 벗어나면 그대로 둔다. */
export function moveItem<T>(root: T, path: Path, index: number, delta: number): T {
  const list = (getPath(root, path) as unknown[] | undefined) ?? [];
  const target = index + delta;
  if (target < 0 || target >= list.length) return root;

  const next = [...list];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return setPath(root, path, next);
}

/**
 * 서버가 준 zod 이슈 경로(`data.notices.0.title`)를 폼 경로(`notices.0.title`)로.
 *
 * 서버는 `{ title, template: { id, data } }` 를 검증하므로 이슈 경로에 접두가 붙는다.
 * 그대로 쓰면 어떤 칸도 맞지 않아 오류가 어디 것인지 알 수 없다.
 */
export function stripIssuePrefix(issuePath: string): string {
  return issuePath.replace(/^template\.data\./, '').replace(/^data\./, '');
}
